import { LoadingButton } from '@atlaskit/button';
import { ErrorMessage } from '@atlaskit/form';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import Select, { CheckboxOption, PopupSelect } from '@atlaskit/select';
import { AkEditorErrorIcon } from '@fuegokit/fuegoicons-react';
import { AtlasButton, Box } from '@fuegokit/react';
import { useCallback, useMemo, useState } from 'react';

import { usePersonalConfiguration } from '../../screens/PersonalConfigurationProvider';
import { useTogglDataContext } from '../../screens/TogglDataProvider';
import { getUserDashboardConfigWithUpdatedWorkspaceProjectsList } from '../../services/personal-config';
import { SelectOption, WorkspaceWithProjects } from '../../types';
import { SelectWrapper } from '../reusable/SelectWrapper';
import { CheckboxGroupLabel } from './CheckboxGroupLabel';

interface WorkspaceAndProjectsSelectorsProps {
  workspacesOptions: SelectOption[];
  selectedWorkspaces: number[];
  selectedWorkspaceOption?: SelectOption;
  allowedProjectIds: number[];
  isDisabled: boolean;
  onChange: (newConfig: WorkspaceWithProjects) => void;
  setIsAddingNewWorkspace: (v: boolean) => void;
}

export function WorkspaceAndProjectsSelectors({
  workspacesOptions,
  selectedWorkspaces,
  selectedWorkspaceOption,
  allowedProjectIds,
  isDisabled,
  onChange,
  setIsAddingNewWorkspace,
}: Readonly<WorkspaceAndProjectsSelectorsProps>) {
  const { updateConfig, config } = usePersonalConfiguration();
  const [isProjectsSelectionCleared, setIsProjectsSelectionCleared] =
    useState(false);
  const [projectsSelectInputValue, setProjectsSelectInputValue] = useState('');
  const { projects } = useTogglDataContext();

  const projectsOptions = useMemo(() => {
    if (!selectedWorkspaceOption) return [];
    const filteredProjects = projects.filter(
      (p) => p.workspace_id === selectedWorkspaceOption.value
    );
    return [
      {
        label: selectedWorkspaceOption.label,
        options: filteredProjects.map((project) => ({
          value: project.id,
          label: project.displayName,
        })),
      },
    ];
  }, [selectedWorkspaceOption, projects]);

  const projectsSelectValue = useMemo(
    () =>
      projectsOptions[0]?.options
        ?.map((option) =>
          allowedProjectIds.includes(Number(option.value)) ||
          !allowedProjectIds.length
            ? option
            : undefined
        )
        .filter(Boolean) || [],
    [projectsOptions.length, allowedProjectIds]
  );

  const isSelectAllProjectsChecked = useMemo(() => {
    return (
      [0, projectsOptions[0]?.options?.length].includes(
        allowedProjectIds.length
      ) && !isProjectsSelectionCleared
    );
  }, [projectsOptions, allowedProjectIds, isProjectsSelectionCleared]);

  const onSelectAllClick = useCallback(() => {
    if (!isSelectAllProjectsChecked || isProjectsSelectionCleared) {
      setIsProjectsSelectionCleared(false);
      void updateConfig(
        getUserDashboardConfigWithUpdatedWorkspaceProjectsList(
          config.userDashboard,
          selectedWorkspaceOption?.value as number,
          []
        )
      );
    } else {
      setIsProjectsSelectionCleared(true);
    }
  }, [
    config,
    updateConfig,
    isSelectAllProjectsChecked,
    selectedWorkspaceOption,
  ]);

  const onProjectsSelectChange = useCallback(
    (options: Readonly<SelectOption[]>) => {
      if (!options.length) {
        setIsProjectsSelectionCleared(true);
      } else {
        setIsProjectsSelectionCleared(false);
        void updateConfig(
          getUserDashboardConfigWithUpdatedWorkspaceProjectsList(
            config.userDashboard,
            selectedWorkspaceOption?.value as number,
            options.map((o) => o.value as number)
          )
        );
      }
    },
    [updateConfig, config, selectedWorkspaceOption]
  );

  const removeWorkspace = useCallback(
    (workspaceId: number | undefined) => {
      if (!workspaceId) {
        setIsAddingNewWorkspace(false);
      } else {
        void updateConfig({
          userDashboard: {
            ...config.userDashboard,
            isSharing:
              selectedWorkspaces.length === 1
                ? false
                : config.userDashboard.isSharing,
            workspaces: config.userDashboard.workspaces.filter(
              (w) => w.id !== workspaceId
            ),
          },
        });
      }
    },
    [updateConfig, config]
  );

  const allProjectsNumber = useMemo(
    () => projectsOptions[0]?.options?.length ?? 0,
    [projectsOptions]
  );

  const selectedProjectsNumberIndicator = useMemo(() => {
    if (!selectedWorkspaceOption) return '';
    const selectedProjectsNumber = isProjectsSelectionCleared
      ? 0
      : projectsSelectValue?.length ?? 0;
    return `(${selectedProjectsNumber}/${allProjectsNumber})`;
  }, [isProjectsSelectionCleared, projectsSelectValue, allProjectsNumber]);

  const filteredProjectsOptions = useMemo(() => {
    if (!projectsOptions[0]) return [];
    const filteredOptions = projectsOptions[0].options.filter((o) =>
      o?.label.toLowerCase().includes(projectsSelectInputValue.toLowerCase())
    );
    const filteredOptionsCut =
      filteredOptions.length > 200
        ? filteredOptions.slice(0, 200)
        : filteredOptions;
    return [{ ...projectsOptions[0], options: filteredOptionsCut }];
  }, [projectsSelectValue, projectsOptions, projectsSelectInputValue]);

  const formatGroupLabel = useCallback(
    () => (
      <CheckboxGroupLabel
        workspaceName={selectedWorkspaceOption?.label ?? ''}
        isChecked={isSelectAllProjectsChecked}
        onClick={onSelectAllClick}
      />
    ),
    [isSelectAllProjectsChecked, onSelectAllClick, selectedWorkspaceOption]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '2',
      }}
    >
      <SelectWrapper
        label="Select Toggl workspace"
        sx={{ flex: 3, justifyContent: 'flex-end' }}
      >
        <Select
          options={workspacesOptions}
          placeholder="Select workspace"
          onChange={(event) =>
            onChange({
              id: event?.value as number,
              allowedProjectIds: [],
            })
          }
          isDisabled={isDisabled}
          value={selectedWorkspaceOption}
          isOptionDisabled={(option) =>
            selectedWorkspaces.includes(option.value as number) &&
            !(option.value === selectedWorkspaceOption?.value)
          }
        />
      </SelectWrapper>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          gap: '1',
          flex: 2,
        }}
      >
        {isProjectsSelectionCleared && (
          <ErrorMessage>At least one project has to be selected</ErrorMessage>
        )}
        <PopupSelect
          components={{ Option: CheckboxOption }}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          isMulti
          options={filteredProjectsOptions}
          placeholder="Select Toggl projects..."
          target={({ isOpen, ...triggerProps }) => (
            <LoadingButton
              {...triggerProps}
              isSelected={isOpen}
              iconAfter={<ChevronDownIcon label="" />}
              isDisabled={isDisabled}
              style={{
                height: '40px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              Select projects {selectedProjectsNumberIndicator}
            </LoadingButton>
          )}
          onChange={onProjectsSelectChange}
          isDisabled={isDisabled}
          formatGroupLabel={formatGroupLabel}
          value={
            isProjectsSelectionCleared
              ? []
              : (projectsSelectValue as SelectOption[])
          }
          controlShouldRenderValue={false}
          isClearable={false}
          inputValue={projectsSelectInputValue}
          onInputChange={setProjectsSelectInputValue}
          minMenuWidth={236}
          isSearchable={true}
          searchThreshold={-1}
        />
      </Box>
      {selectedWorkspaces.length > 1 ? (
        <AtlasButton
          appearance="subtle"
          leadingIcon={AkEditorErrorIcon}
          sx={{
            height: '40px',
            marginTop: '28px',
          }}
          onClick={() =>
            removeWorkspace(
              selectedWorkspaceOption?.value as number | undefined
            )
          }
          isDisabled={isDisabled}
        />
      ) : (
        <Box
          sx={{
            width: '7',
            height: '40px',
          }}
        ></Box>
      )}
    </Box>
  );
}
